// Import React hooks
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

// Import context function
import { AuthContext } from "global/context/AuthContext";

// Import strich api calls
import {
    getAIWorkflows,
    addAIGenerationRequest,
    getAIGenerationQueueItem,
    deleteAIGenerationQueueItem,
} from "api/APIStrichCalls";

// Import hooks
import { useToast } from "global/hooks/useToast";



// Export the custom hook
export default function UsePodDesignAiFlow({
    setSubmittedAiDesign,
    setShowPodDesignAiPage,
}) {

    // Get the token from AuthContext
    const { token, authReady } = useContext(AuthContext);

    // Define toast options
    const { toastError } = useToast();

    // Store the selectedWorkflow
    const [selectedWorkflow, setSelectedWorkflow] = useState(null);

    // This stores the current visible step in the wizard
    const [currentStep, setCurrentStep] = useState("generate");

    // This stores the user prompt
    const [prompt, setPrompt] = useState("");

    // This stores the design title
    const [title, setTitle] = useState("");

    // This stores the design title
    const [description, setDescription] = useState("");

    // This stores the design title
    const [keywords, setKeywords] = useState("");

    // This stores the selected design type
    const [designType, setDesignType] = useState("");

    // This stores the selected model id
    const [selectedModelId, setSelectedModelId] = useState("");

    // This stores the selected style id
    const [selectedStyleId, setSelectedStyleId] = useState("");

    // This stores the selected tag/describe mode
    const [tagDescribeMode, setTagDescribeMode] = useState("");

    // This stores the selected aspect ratio
    const [selectedAspectRatio, setSelectedAspectRatio] = useState("");

    // This stores model options from the workflow call
    const [modelOptions, setModelOptions] = useState([]);

    // This stores style options from the workflow call
    const [styleOptions, setStyleOptions] = useState([]);

    // This stores design type options from the workflow call
    const [designTypeOptions, setDesignTypeOptions] = useState([]);

    // This stores tag & describe options from the workflow call
    const [tagDescribeOptions, setTagDescribeOptions] = useState([]);

    // This stores aspect ratio options from the workflow call
    const [aspectRatioOptions, setAspectRatioOptions] = useState([]);

    // This stores the generated AI image
    const [generatedImage, setGeneratedImage] = useState(null);

    // This stores the optimised print-ready image
    const [optimisedImage, setOptimisedImage] = useState(null);

    // This tracks whether generation is loading
    const [isGenerating, setIsGenerating] = useState(false);

    // This tracks whether optimisation is loading
    const [isOptimising, setIsOptimising] = useState(false);

    // This tracks whether setup data is loading
    const [isLoadingSetupData, setIsLoadingSetupData] = useState(false);

    // This stores any generation error message
    const [generateError, setGenerateError] = useState("");

    // This stores any optimisation error message
    const [optimiseError, setOptimiseError] = useState("");

    // This stores any setup error message
    const [setupError, setSetupError] = useState("");

    // Set image queue id
    const [generatedImageJobID, setGeneratedImageJobID] = useState(null);

    // Set image queue id
    const [optimisedImageJobID, setOptimisedImageJobID] = useState(null);

        


    // This maps style codes to local preview images
    const stylePreviewImageMap = useMemo(() => {

        return {

            comic: "/images/ai/styles/comic_book.png",
            pixel_art: "/images/ai/styles/pixel_art.png",
            anime: "/images/ai/styles/anime.png",
            lineart: "/images/ai/styles/line_art.png",
            realistic: "/images/ai/styles/realistic.png",
            watercolour: "/images/ai/styles/watercolour.png",
            none: "",
            //none: "/images/ai/styles/none.png",

            // This is the no-style preview as an inline SVG stop symbol
            //none: `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="34" fill="none" stroke="%23d92d20" stroke-width="10"/><line x1="26" y1="74" x2="74" y2="26" stroke="%23d92d20" stroke-width="10" stroke-linecap="round"/></svg>`,

            };

    }, []);

    

    // This maps aspect ratio codes to shape metadata instead of image paths
    const aspectRatioPreviewImageMap = useMemo(() => {

        return {

            // This is a perfect square
            square: {
                label: "Square",
                width: 1,
                height: 1,
            },

            // This is A4 portrait ratio
            portrait: {
                label: "Portrait",
                width: 210,
                height: 297,
            },

            // This is A4 landscape ratio
            landscape: {
                label: "Landscape",
                width: 297,
                height: 210,
            },

        };

    }, []);



    // This finds a setting from the workflow settings array
    const findWorkflowSetting = (settingsArray, settingKey) => {

        // Return the matching setting object
        return settingsArray?.find((settingItem) => settingItem.setting === settingKey) || null;
    };



    // This converts API option objects into select field options
    const mapToSelectOptions = (optionsArray) => {

        // Return mapped options
        return (optionsArray || []).map((option) => ({
            value: option.code,
            label: option.name,
        }));
        
    };



    // This converts style options into image card options
    const mapToStyleImageOptions = (optionsArray) => {

        // Return mapped style options
        return (optionsArray || []).map((option) => ({
            id: option.code,
            label: option.name,
            previewImage: stylePreviewImageMap[option.code] || "/images/ai/styles/style-fallback.png",
        }));

    };



    // This converts aspect ratio options into image card options
    const mapToAspectRatioImageOptions = (optionsArray) => {

        // Return mapped ratio options, excluding square
        return (optionsArray || [])

            // Remove square option
            .filter((option) => option?.code !== "square")

            // Map remaining options
            .map((option) => ({

                id: option.code,
                label: option.name,
                aspectRatio: aspectRatioPreviewImageMap[option.code] || "/images/ai/aspect-ratios/aspect-ratio-fallback.png",

            }));

    };



    // This loads the setup options when the component mounts
    useEffect(() => {

        // This loads workflow data
        const loadSetupData = async () => {

            // Turn loading on
            setIsLoadingSetupData(true);

            // Clear old setup error
            setSetupError("");

            try {

                // Make the call 
                const response = await getAIWorkflows(token);

                // Guard against bad response
                if (!response?.success || !Array.isArray(response?.results)) {

                    throw new Error("Invalid workflows response");

                }

                // Find the pod workflow
                const podWorkflow = response.results.find((workflowItem) => workflowItem.code === "pod");

                // Stop if pod or upscale workflow is missing
                if (!podWorkflow) {

                    throw new Error("POD workflow not found");

                }

                // Get the settings array safely
                const podSettings = podWorkflow.settings || [];

                setSelectedWorkflow(podSettings);

                // Find each needed setting object
                const designTypeSetting = findWorkflowSetting(podSettings, "design_type");
                const modelSetting = findWorkflowSetting(podSettings, "model");
                const styleSetting = findWorkflowSetting(podSettings, "style");
                const tagDescribeSetting = findWorkflowSetting(podSettings, "tag_describe");
                const aspectRatioSetting = findWorkflowSetting(podSettings, "aspect_ratio");

                // Map design type options for the dropdown
                const nextDesignTypeOptions = mapToSelectOptions(designTypeSetting?.options);

                // Map model options for the dropdown
                const nextModelOptions = mapToSelectOptions(modelSetting?.options);

                // Map style options for image cards
                const nextStyleOptions = mapToStyleImageOptions(styleSetting?.options);

                // Map tag & describe options for the dropdown
                const nextTagDescribeOptions = mapToSelectOptions(tagDescribeSetting?.options);

                // Map aspect ratio options for image cards
                const nextAspectRatioOptions = mapToAspectRatioImageOptions(aspectRatioSetting?.options);

                // Save the options into state
                setDesignTypeOptions(nextDesignTypeOptions);
                setModelOptions(nextModelOptions);
                setStyleOptions(nextStyleOptions);
                setTagDescribeOptions(nextTagDescribeOptions);
                setAspectRatioOptions(nextAspectRatioOptions);

                // Prefill defaults from workflow default values
                setDesignType(designTypeSetting?.default_value || designTypeSetting?.options?.[0]?.code || "");
                setSelectedModelId( modelSetting?.default_value || modelSetting?.options?.[0]?.code || "");
                setSelectedStyleId( styleSetting?.default_value || styleSetting?.options?.[0]?.code || "");
                setTagDescribeMode(tagDescribeSetting?.default_value || tagDescribeSetting?.options?.[0]?.code || "");
                
                // Force 'portrait' as first selection (only available on E2E) as we're not showing square which is the default option
                setSelectedAspectRatio("square");

                //console.log("POD workflow loaded:", podWorkflow);

            } catch (error) {

                // Save setup error
                setSetupError("We could not load the POD AI options. Please try again.");

            } finally {

                // Turn loading off
                setIsLoadingSetupData(false);

            }
        };

        // Run the setup loading
        loadSetupData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stylePreviewImageMap, aspectRatioPreviewImageMap]);



    // This checks if generate can run
    const canGenerate =
        prompt.trim().length > 0 &&
        isGenerating === false &&
        isLoadingSetupData === false &&
        !!designType &&
        !!selectedModelId &&
        !!selectedStyleId &&
        !!tagDescribeMode &&
        !!selectedAspectRatio;



    // This checks if optimise can run
    const canOptimise =
        !!generatedImage &&
        isOptimising === false;



    // This checks if submit can run
    const canSubmit =
        !!optimisedImage &&
        title.trim().length > 0;
   

    // This checks if the current selected settings match the generatedImage
    const formSettingsMatchImageSettings = 
        prompt === generatedImage?.settings?.positive_design_prompt &&
        designType === generatedImage?.settings?.design_type &&
        selectedModelId === generatedImage?.settings?.model &&
        selectedStyleId === generatedImage?.settings?.style 
        //&& (designType === "edge-to-edge" && selectedAspectRatio === generatedImage?.settings?.aspect_ratio);

    



    // Fetch queue item and update polling state based on in-progress job
    const fetchImage = useCallback(async (currentStep, jobID) => {

        //console.log("fetchImage: ", currentStep, jobID);

        try {
    
            // Fetch the AI generation image we're tracking
            const queueResponse = await getAIGenerationQueueItem(token, jobID);

            //console.log("queueResponse: ", queueResponse);

            // Check if the call was successful
            if (queueResponse.success) {

                // Get the queue item from the response, defaulting to an empty array if not present
                const queueImage = queueResponse.results[0] || null;

                // Check whether the queue item has failed
                const hasQueueError = Boolean(queueImage?.error_message) || String(queueImage?.status || "").toLowerCase() === "errored";

                if (hasQueueError) {

                    // Start with no error text so we can build the message supplied by the API
                    let queueErrorMessage = "";

                    // Use a plain string error message directly
                    if (typeof queueImage?.error_message === "string" && queueImage.error_message.trim()) {

                        queueErrorMessage = queueImage.error_message.trim();

                    }

                    // Use the detailed error message when the API returned an error object
                    if (!queueErrorMessage && queueImage?.error_message?.details) {

                        queueErrorMessage = queueImage.error_message.details;

                    }

                    // Append the workflow mapping error when one was supplied
                    if (queueImage?.error_message?.map_error) {

                        queueErrorMessage += (queueErrorMessage ? " " : "") + queueImage.error_message.map_error;

                    }

                    // Fall back to a useful message if the queue item only supplied an errored status
                    if (!queueErrorMessage) {

                        queueErrorMessage = "The AI image generation job failed.";

                    }

                    if (currentStep === "generating") {

                        // Show the generation error to the user
                        setGenerateError(queueErrorMessage);

                        // Stop polling this failed job
                        setIsGenerating(false);
                        setGeneratedImageJobID(null);

                        // Clear the failed queue item so the generate button returns to its normal state
                        setGeneratedImage(null);

                        // Return to the generate screen so the user can try again
                        setCurrentStep("generate");

                    }

                    if (currentStep === "optimising") {

                        // Store the optimisation error
                        setOptimiseError(queueErrorMessage);

                        // Stop polling this failed job
                        setIsOptimising(false);
                        setOptimisedImageJobID(null);

                        // Clear the failed queue item while keeping the generated image available
                        setOptimisedImage(null);

                        // Return to the optimise screen so the user can try again
                        setCurrentStep("optimise");

                    }

                    // Do not process an errored queue item as a completed image
                    return;

                }

                // An item is in progress if it doesn't have a final image, error and had a progress < 100% 
                const isInProgress = !queueImage?.image_url && Number(queueImage?.progress) < 100;

                if (isInProgress) {

                    if (currentStep === "generating") {

                        // Set the loading state
                        setIsGenerating(true);

                        // Set generated image (so we can access progress data)
                        setGeneratedImage(queueImage);

                    }

                    if (currentStep === "optimising") {

                        // Set the loading state
                        setIsOptimising(true);

                        // Set generated image (so we can access progress data)
                        setOptimisedImage(queueImage);

                    }
                    
                    return;

                } else {

                    // We have the final image

                    if (currentStep === "generating") {
                    
                        // Set the generated image
                        setGeneratedImage(queueImage);

                        // Set the current step to optimise as that's the next step
                        setCurrentStep("optimise");

                        // Clear the loading state
                        setIsGenerating(false);

                    }

                    if (currentStep === "optimising") {
                    
                        // Set the generated image
                        setOptimisedImage(queueImage);

                        // Set the description with the tag_describe response
                        setDescription(queueImage.output?.description || null);

                        // Create keyword list array
                        const keywordList = (

                            // Check keywords exist and trim them
                            queueImage.output?.tags?.trim()

                                // They do, so get the keywords
                                ? queueImage.output?.tags

                                    // Split each keyword/phrase by comma
                                    .split(',')

                                    // Map the keywords and trim any trailing spaces
                                    .map(keyword => keyword.trim())

                                    // Filter only real values (with a length greater than 0)
                                    .filter(keyword => keyword.length > 0)

                                // Else create an empyty array
                                : []

                        );

                        // Set the keywords with the formatted tag_describe response
                        setKeywords({keywords: keywordList});

                        // Set the current step to review as that's the next step
                        setCurrentStep("review");

                        // Clear the loading state
                        setIsOptimising(false);

                    }

                }

            } else {

                // Build a useful error message when the queue lookup itself failed
                const queueLookupErrorMessage = typeof queueResponse?.error_message === "string" && queueResponse.error_message.trim()
                    ? queueResponse.error_message.trim()
                    : "Could not check the AI image generation progress.";

                // Send the failed queue lookup through the normal polling error handling
                throw new Error(queueLookupErrorMessage);

            }
    
        } catch (error) {
          
            // Log error to console and stop polling on error
            console.error("Error fetching image data:", error);
          
            if (currentStep === "generating") {

                // Show the polling error to the user
                setGenerateError(error?.message || "Could not check the AI image generation progress.");

                // Stop polling
                setIsGenerating(false);

                // Go back to generate screen
                setCurrentStep("generate");
                
                // Clear image state
                setGeneratedImage(null);

            }

            if (currentStep === "optimising") {

                // Store the polling error
                setOptimiseError(error?.message || "Could not check the AI image optimisation progress.");

                // Stop polling
                setIsOptimising(false);

                // Go back to generate screen
                setCurrentStep("optimise");
                
                // Clear image state
                setOptimisedImage(null);

            }
    
            // Return empty array on error
            return [];
    
        }

    }, [token]);



    // Poll every 2 seconds only while there are active queue jobs
    useEffect(() => {

        //console.log("start polling.................", currentStep, generatedImageJobID);

        // Don't start polling until auth is ready, there is an item to track and we have the job ID
        if (
            !authReady 
            || (currentStep === "generating" && (!isGenerating || !generatedImageJobID))  
            || (currentStep === "optimising" && ( !isOptimising || !optimisedImageJobID))    
            || currentStep === "generate"
            || currentStep === "optimise"
            || currentStep === "review"
        ) return undefined;

        //console.log("start polling.................", currentStep, generatedImageJobID);

        const jobID = isGenerating ? generatedImageJobID : isOptimising ? optimisedImageJobID : null;
        
        // Start polling interval
        const interval = setInterval(() => {

            // Fetch the latest queue data on each interval tick
            fetchImage(currentStep, jobID);

        }, 2000);

        return () => clearInterval(interval);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authReady, isGenerating, generatedImageJobID, isOptimising, optimisedImageJobID]);



    // This handles AI image generation
    const handleGenerateImage = async () => {

        // Set current step
        setCurrentStep("generating");

        // Clear old generate errors
        setGenerateError("");

        // Clear old generated image
        setGeneratedImage(null);

        // Clear old optimised image
        setOptimisedImage(null);

        // Turn loading on
        setIsGenerating(true);

        // Build only the POD settings needed for this wizard
        const normalizedSettings = {
            positive_design_prompt: prompt,
            design_type: designType,
            model: selectedModelId,
            style: selectedStyleId,

            //tag_describe: tagDescribeMode, // removed as requested by Ben

            // Force 'square' aspect ratio if the design type is standalone
            aspect_ratio: designType === "standalone" ? "square" : selectedAspectRatio,

            // Hardcode 'standard' quality (fast initial image gen)
            quality: "standard_quality",
        };

        // Build payload with normalized settings and workflow code
        const payload = {
            settings: normalizedSettings,
            workflow_code: selectedWorkflow?.code || "pod",
        };

        // Submit workflow request
        const response = await addAIGenerationRequest(token, payload);

        //console.log("addAIGenerationRequest: ", response);

        // Handle success
        if (response?.success) {

            // Set the jod id so we can look for it in the queue
            setGeneratedImageJobID(response.results[0].job);

            // Set the title with the prompt
            setTitle(prompt);

            // Set waiting state because the workflow has started, but the generated image is not ready yet
            setIsGenerating(true);

            // Set the current step to generating (loading screen)
            setCurrentStep("generating");

        } else {

            // Toast error
            toastError({title: "Error", message: "Could not run workflow: " + (response?.error_message || "Unknown error"), autoCloseMs: 6000});

            // Save generation error
            setGenerateError("Could not run workflow: " + (response?.error_message || "Unknown error"));

            // Clear generating flag
            setIsGenerating(false);

        }
        
    };



    // Reset to the generated images settings
    const handleResetImageSettings = () => {

        setPrompt(generatedImage?.settings?.positive_design_prompt);
        setDesignType(generatedImage?.settings?.design_type);
        setSelectedModelId(generatedImage?.settings?.model);
        setSelectedStyleId(generatedImage?.settings?.style);
        setSelectedAspectRatio(generatedImage?.settings?.aspect_ratio);

    };

    
    // This discards the current generated image and resets step 1
    const handleDiscardGeneratedImage = async () => {

        const response = await deleteAIGenerationQueueItem(token, generatedImageJobID);

        if (response.success) {

            // Clear generated image
            setGeneratedImage(null);

            // Clear optimised image
            setOptimiseError("");
            setOptimisedImage(null);

            // Return to step 1
            setCurrentStep("generate");

        }
       
    };



    // This moves the user back to generate
    const handleBackToGenerate = () => {

        // Clear generated image
        setGeneratedImage(null);

        // Clear optimised image
        setOptimiseError("");
        setOptimisedImage(null);

        // Return to step 1
        setCurrentStep("generate");

    };



    // This handles optimisation
    const handleOptimiseImage = async () => {

        // Stop if there is no generated image or no seed number
        if (!generatedImage || !generatedImage?.settings?.seed ) {
            
            return;

        }

        // Clear old optimise errors
        setOptimiseError("");

        // Move to the optimise step
        setCurrentStep("optimising");

        // Turn loading on
        setIsOptimising(true);

        // Build only the POD settings needed for this work flow using the generated image data
        const normalizedSettings = {
            positive_design_prompt: generatedImage.settings?.positive_design_prompt,
            design_type: generatedImage.settings?.design_type,
            model: generatedImage.settings?.model,
            style: generatedImage.settings?.style,
            //tag_describe: tagDescribeMode, // Removed per Ben's request

            // Force 'square' aspect ratio if the design type is standalone
            aspect_ratio: generatedImage.settings?.design_type === "standalone" ? "square" : generatedImage.settings.aspect_ratio,

            // Hardcode 'high' quality (slow final print generation)
            quality: "high_quality",

            // Keep the seed number generated in the first inital image
            seed: generatedImage.settings.seed,

        };

        // Build payload with normalized settings and workflow code
        const payload = {
            settings: normalizedSettings,
            workflow_code: selectedWorkflow?.code || "pod",
        };

        // Submit workflow request
        const response = await addAIGenerationRequest(token, payload);

        // Handle success
        if (response?.success) {

            // Set the jod id so we can look for it in the queue
            setOptimisedImageJobID(response.results[0].job);

            // Set waiting state because the workflow has started, but the optimised image is not ready yet
            setIsOptimising(true);

            // Set the current step to optimising (loading screen)
            setCurrentStep("optimising");

        } else {

            // Toast error
            toastError({title: "Error", message: "Could not optimise this image for print:" + (response?.error_message || "Unknown error"), autoCloseMs: 6000});


            // Save optimise error
            setOptimiseError("We could not optimise this image for print. Please try again.");

            // Clear optimising flag
            setIsOptimising(false);

        }

    };


    
    // This discards the current generated image and resets step 1
    const handleDiscardGeneratedAndOptimisedImage = async () => {

        // Check we have a generated image
        if (generatedImage) {

            // Make the call to delete the generated image
            const deleteGeneratedImageResponse = await deleteAIGenerationQueueItem(token, generatedImageJobID);

            // Check if it was successful
            if (deleteGeneratedImageResponse.success) {

                // Make the call to delete the optimised image
                const deleteOptimisedImageResponse = await deleteAIGenerationQueueItem(token, optimisedImageJobID);

                // Check if it was successful
                if (deleteOptimisedImageResponse.success) {

                    // Clear generated image
                    setGeneratedImage(null);
                    setGenerateError("");

                    // Clear optimised image
                    setOptimiseError("");
                    setOptimisedImage(null);

                    // Return to step 1
                    setCurrentStep("generate");

                } else {
                   
                    // Toast error
                    toastError({title: "Error", message: "Could not delete optimised image:" + (deleteOptimisedImageResponse?.error_message || "Unknown error"), autoCloseMs: 6000});


                }

            } else {

                // Toast error
                toastError({title: "Error", message: "Could not delete optimised image:" + (deleteGeneratedImageResponse?.error_message || "Unknown error"), autoCloseMs: 6000});


            }

        } else {

            // There's no generatedImage so just delete the optimised image
            const deleteOptimisedImageResponse = await deleteAIGenerationQueueItem(token, optimisedImageJobID);

            // Check if it was successful
            if (deleteOptimisedImageResponse.success) {

                // Clear generated image
                setGeneratedImage(null);
                setGenerateError("");

                // Clear optimised image
                setOptimiseError("");
                setOptimisedImage(null);

                // Return to step 1
                setCurrentStep("generate");
            
            } else {

                // Toast error
                toastError({title: "Error", message: "Could not delete optimised image:" + (deleteOptimisedImageResponse?.error_message || "Unknown error"), autoCloseMs: 6000});


            }

        } 
       
    };



    // This submits the final design payload
    const handleSubmitDesign = async () => {

        // Stop if the data is incomplete
        if (!optimisedImage || !title.trim()) {

            return;

        }

        // Build the final payload
        const payload = {
            title: title.trim(),
            description: description,
            // Ensure keywords is passed back as a string
            keywords: Array.isArray(keywords?.keywords)
                ? keywords.keywords.join(", ")
                : keywords?.keywords || " ",
            imageDetails: {
                image_url: optimisedImage.image_url,
                thumbnail_url: optimisedImage.thumbnail_url,

                // Use the definitive stored type from the completed AI bucket result
                file_type: optimisedImage?.bucket?.file_type || null,

                // Keep the stored result metadata including its dimensions
                metadata: optimisedImage?.bucket?.metadata || null,

                source: "ai-generated",
            },
            uploadedImage: optimisedImage.image_url,
            prompt: prompt,
            settings: {
                workflow: "pod",
                positive_design_prompt: prompt,
                design_type: designType,
                model: selectedModelId,
                style: selectedStyleId,
                //tag_describe: tagDescribeMode, // Removed per Ben's request
                aspect_ratio: selectedAspectRatio,
            },
        };

        // Return the completed design by setting the passed in state
        setSubmittedAiDesign((prev) => {

            // Ensure we always have an object
            const safePrevDetails = prev ?? {};

            // Return the updated object
            return {

                // Keep all previous fields
                ...safePrevDetails,

                // Add our payload
                payload

            };

        });
        
        // Close the popup
        setShowPodDesignAiPage(false);

    };



    // A list of colours we can map to show as a background colour to the image to check it's quality / white areas
    const previewBackgroundColours = [
        {
            label: "Transparent",
            colour: "none",
        },
        {
            label: "White",
            colour: "#fff",
        },
        {
            label: "Red",
            colour: "#ec0707",
        },
        {
            label: "Blue",
            colour: "#125ae3",
        },
        {
            label: "Green",
            colour: "#33a71b",
        },
        {
            label: "Black",
            colour: "#000",
        }
    ];



   
    // Handle keywords changes (adding / removing)
    const handleKeywordsChange = (newKeywordsArray) => {
        
        // Update the keywords state as an array
        setKeywords({
            keywords: newKeywordsArray
        });

    };



    // Return everything the page needs
    return {
        currentStep,
        prompt,
        setPrompt,
        title,
        setTitle,
        description, 
        setDescription,
        keywords,
        handleKeywordsChange,
        designType,
        setDesignType,
        selectedModelId,
        setSelectedModelId,
        selectedStyleId,
        setSelectedStyleId,
        tagDescribeMode,
        setTagDescribeMode,
        selectedAspectRatio,
        setSelectedAspectRatio,
        designTypeOptions,
        modelOptions,
        styleOptions,
        tagDescribeOptions,
        aspectRatioOptions,
        generatedImage,
        optimisedImage,
        isGenerating,
        isOptimising,
        isLoadingSetupData,
        generateError,
        optimiseError,
        setupError,
        canGenerate,
        canOptimise,
        canSubmit,
        handleGenerateImage,
        handleResetImageSettings,
        handleDiscardGeneratedImage,
        handleDiscardGeneratedAndOptimisedImage,
        handleOptimiseImage,
        handleBackToGenerate,
        handleSubmitDesign,
        previewBackgroundColours,
        formSettingsMatchImageSettings,
    };
}